import { useCallback } from 'react'
import DashboardField from "DashboardUser/components/DashboardField"
import Filters from "DashboardUser/components/Filters"
import { useAct } from 'DashboardUser/store'
import { isMobile } from 'react-device-detect';
import RecordModal from 'DashboardUser/components/RecordModal';
import { CircularProgress } from "@mui/material";

export default function DashboardTargetTable({ preview = false }) {
  const act = useAct();
  const dataLoading = useSel(s => s.local.dataLoading);

  // Getting data from your state management
  const dashboardTargets = useSel(s => _.values(s.dashboardTargets.entities));
  const { currentPage, totalPages, expandedDashboardTargetId } = useSel(s => s.local);
  const dashboardFields = useSel(s => _.orderBy(s.dashboard.dashboardFields, 'digitOrder', 'asc'));

  // Optional: Sort functionality
  // TODO: put this onto the server backend!
  const [sortFieldId, setSortFieldId] = React.useState(1);
  const [sortDirection, setSortDirection] = React.useState('asc');

  const handleSort = (field) => {
    // If clicking the same field, toggle direction
    if (field === sortFieldId) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // New field, set to ascending by default
      setSortFieldId(field);
      setSortDirection('asc');
    }
  };

  // Sort the targets based on the selected field and direction
  const sortedTargets = useMemo(() => {
    return dashboardTargets.sort((a, b) => {
      if (a.fields[sortFieldId] < b.fields[sortFieldId]) return sortDirection === 'asc' ? -1 : 1;
      if (a.fields[sortFieldId] > b.fields[sortFieldId]) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [dashboardTargets, sortFieldId, sortDirection]);

  // Pagination
  const handlePageChange = (page) => {
    page = Math.max(0, Math.min(page, totalPages));
    act.local.upsert({ currentPage: page });
  }

  return (
    <>
      <div className="team-table-container box">
        {!preview && <>
          <div className="team-table-header">
            <h1>Members</h1>
            {dataLoading && <div className="loading-indicator">
              <CircularProgress size={24} />
            </div>}
          </div>
          <div>
            <Filters />
          </div>
        </>}
        <table className="team-table">
          <thead>
            <tr>
              <th className='meta-col'></th>
              {dashboardFields.map(df => <th
                key={df.id}
                onClick={() => handleSort(df.id)}
                className={sortFieldId === df.name ? `sort-${sortDirection}` : ''}
              >
                <span className="field-name">{df.name}</span>
                <SvgIconsSort asc={sortFieldId === df.id && sortDirection === 'asc'} desc={sortFieldId === df.id && sortDirection === 'desc'} />
              </th>)}
            </tr>
          </thead>
          <tbody>
            {sortedTargets.map((dashboardTarget, index) => (
              <DashboardTargetRow
                key={dashboardTarget.id || index}
                dashboardTargetId={dashboardTarget.id}
              />
            ))}
          </tbody>
        </table>
        {dashboardTargets.length === 0 && <div className='no-data'>No members found with current filters.</div>}
        {dashboardTargets.length > 0 && !preview && <div className="team-table-footer">
          <div className="pagination">
            <button className='button pagination-prev' onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}>
              <SvgIconsBackCaret />
            </button>
            <div className="pagination-pages">
              <input type="text" className='pagination-current' value={currentPage} onChange={e => handlePageChange(Number(e.target.value))} />
              <span className='pagination-text'>of</span>
              <span className='pagination-total'>{totalPages}</span>
            </div>
            <button className='button pagination-next' onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages}>
              <SvgIconsForwardCaret />
            </button>
          </div>
        </div>}
      </div>
      {expandedDashboardTargetId && <RecordModal />}
    </>
  );
}

const DashboardTargetRow = ({ dashboardTargetId }) => {
  const act = useAct();
  const expanded = useSel(s => s.local.expandedDashboardTargetId === dashboardTargetId);
  const dashboardTarget = useSel(s => s.dashboardTargets.entities[dashboardTargetId]);
  const dashboardFields = useSel(s => _.orderBy(s.dashboard.dashboardFields, 'digitOrder'));

  const [fieldBackgroundColors, setFieldBackgroundColors] = useState({});

  const toggleExpanded = () => {
    if (expanded) {
      act.local.upsert({ expandedDashboardTargetId: null });
    } else {
      act.local.upsert({ expandedDashboardTargetId: dashboardTargetId });
    }
  }

  const handleBackgroundColorChange = useCallback((fieldId, backgroundColor) => {
    setFieldBackgroundColors(prev => ({
      ...prev,
      [fieldId]: backgroundColor
    }));
  }, []);

  return <tr className={`${expanded ? 'expanded' : ''}`} onClick={() => isMobile ? toggleExpanded() : null}>
    <td className="expand-row-icon meta-col" onClick={toggleExpanded}>
      {expanded ? <SvgIconsClose /> : <SvgIconsEdit color={`rgba(38, 38, 77, 0.6)`} />}
    </td>
    {dashboardFields.map(df =>
      <td key={df.id} className={fieldBackgroundColors[df.id] || ''}>
        <DashboardField
          field={df}
          dashboardTarget={dashboardTarget}
          onBackgroundColorChange={useCallback((color) => handleBackgroundColorChange(df.id, color), [df.id, handleBackgroundColorChange])}
        />
      </td>
    )}
  </tr>
};
